'use client';

import { ArrowLeft, ArrowRight, CheckCircle2, Minus, Plus } from 'lucide-react';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import type { CartSubselection } from '@/features/totems/domain/model/cart-item';
import type { MenuSnapshotProduct } from '@/features/totems/domain/model/menu-snapshot';
import { getProductDescription, getProductDisplayName } from '@/features/totems/domain/service/product-name';
import {
  getProductStockLimit,
  isProductOutOfStock,
  shouldShowProduct,
} from '@/features/totems/domain/service/product-stock';
import { formatTotemPrice } from '@/features/totems/domain/service/totem-price';
import { useProductInteraction } from '@/features/totems/presentation/hooks/web-totem/use-product-interaction';
import { useTotemActions, useTotemCartState } from '@/features/totems/presentation/providers/web-totem/totem-provider';
import type { Database } from '@/shared/infrastructure/supabase/types';
import { cn } from '@/shared/utils/utils';

type SubselectionItem = Database['public']['Tables']['subselection_item']['Row'] & {
  product_stock?: Database['public']['Tables']['product_stock']['Row'] | null;
};

function SubselectionGroup({
  groupIndex,
  name,
  minSelection,
  maxSelection,
  single,
  items,
  selected,
  onChange,
}: {
  groupIndex: number;
  name: string;
  minSelection: number;
  maxSelection: number;
  single: boolean;
  items: SubselectionItem[];
  selected: Record<string, number>;
  onChange: (itemId: string, quantity: number) => void;
}) {
  const totalSelected = Object.values(selected).reduce((sum, qty) => sum + qty, 0);

  const groupDescription = minSelection > 0 ? `Elegí al menos ${minSelection} opciones` : 'Opcional';

  return (
    <div className="mt-6 flex flex-col gap-4 first:mt-0">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-700 font-bold text-white">
          {groupIndex}
        </div>
        <div className="flex flex-col">
          <h3 className="text-lg font-bold">{name}</h3>
          <span className="text-sm text-muted-foreground">{groupDescription}</span>
        </div>
      </div>

      {/* Content */}
      {single ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {items.map(item => {
            const isSelected = (selected[item.id] ?? 0) > 0;
            const isOutOfStock = isProductOutOfStock(item.product_stock);
            const isMaxReached = maxSelection > 0 && totalSelected >= maxSelection;
            // Prevent selection if out of stock, OR if it's not selected and we've hit the limit (unless maxSelection === 1, where clicking just swaps it)
            const isDisabled = isOutOfStock || (!isSelected && isMaxReached && maxSelection !== 1);

            return (
              <button
                key={item.id}
                type="button"
                disabled={isDisabled}
                className={cn(
                  'relative flex flex-col items-center gap-3 rounded-xl border-2 p-3 transition-all outline-none',
                  isDisabled && !isSelected
                    ? 'cursor-not-allowed border-transparent bg-muted/20 opacity-50 grayscale'
                    : '',
                  isOutOfStock
                    ? 'cursor-not-allowed border-transparent bg-muted/20 opacity-50 grayscale'
                    : isSelected
                      ? 'border-emerald-500 bg-emerald-500/5'
                      : 'border-transparent bg-muted/30 hover:bg-muted/50'
                )}
                onClick={() => {
                  if (isSelected) {
                    onChange(item.id, 0);
                  } else {
                    if (maxSelection === 1) {
                      // Deselect others
                      items.forEach(i => {
                        if (i.id !== item.id && selected[i.id]) {
                          onChange(i.id, 0);
                        }
                      });
                    } else if (isMaxReached) {
                      return; // Prevent selection if max is reached
                    }
                    onChange(item.id, 1);
                  }
                }}
              >
                {/* Image Container */}
                <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-white/50">
                  {isSelected && (
                    <div className="absolute top-2 left-2 z-10 rounded-full bg-white text-emerald-500 shadow-sm">
                      <CheckCircle2 className="size-6 fill-emerald-500 text-white" />
                    </div>
                  )}
                  {item.price > 0 && (
                    <div className="absolute top-2 right-2 z-10 rounded-full bg-orange-500 px-2 py-0.5 text-xs font-bold text-white shadow-sm">
                      +${item.price}
                    </div>
                  )}
                  {item.image_url ? (
                    <Image src={item.image_url} alt={item.name} fill className="object-cover" sizes="150px" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gray-100 p-2 text-center text-sm font-medium text-muted-foreground">
                      {item.name}
                    </div>
                  )}
                </div>

                {/* Name */}
                <span
                  className={cn(
                    'line-clamp-2 min-h-[2.5rem] text-center text-sm leading-tight font-medium',
                    isOutOfStock && 'text-muted-foreground line-through decoration-muted-foreground'
                  )}
                >
                  {item.name}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-2 rounded-2xl bg-muted/30 p-2">
          {items.map((item, index) => {
            const qty = selected[item.id] ?? 0;
            const isLast = index === items.length - 1;
            const isOutOfStock = isProductOutOfStock(item.product_stock);
            const isMaxReached = maxSelection > 0 && totalSelected >= maxSelection;

            return (
              <div
                key={item.id}
                className={cn('flex flex-col gap-2 p-3', !isLast && 'border-b', isOutOfStock && 'opacity-50 grayscale')}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-1 flex-col">
                    <span className={cn('text-base font-medium', isOutOfStock && 'text-muted-foreground line-through')}>
                      {item.name}
                    </span>
                    {item.price > 0 && (
                      <span className="text-sm text-muted-foreground">+{formatTotemPrice(item.price)}</span>
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      disabled={qty <= 0 || isOutOfStock}
                      onClick={() => onChange(item.id, Math.max(0, qty - 1))}
                      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red-500 text-white transition-colors disabled:bg-gray-300 disabled:opacity-50"
                    >
                      <Minus className="size-4" />
                    </button>

                    <span className="w-4 text-center text-lg font-semibold">{qty}</span>

                    <button
                      type="button"
                      disabled={
                        isOutOfStock || (isMaxReached && maxSelection !== 1) || (maxSelection === 1 && qty >= 1)
                      }
                      onClick={() => {
                        if (maxSelection === 1 && qty === 0) {
                          items.forEach(i => {
                            if (i.id !== item.id && (selected[i.id] ?? 0) > 0) {
                              onChange(i.id, 0);
                            }
                          });
                          onChange(item.id, 1);
                        } else {
                          onChange(item.id, qty + 1);
                        }
                      }}
                      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white transition-colors disabled:bg-gray-300 disabled:opacity-50"
                    >
                      <Plus className="size-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function buildSubselectionPayload(
  product: MenuSnapshotProduct,
  selections: Record<string, Record<string, number>>
): CartSubselection[] {
  const result: CartSubselection[] = [];

  for (const ps of product.product_subselection ?? []) {
    const groupSelections = selections[ps.subselection_id] ?? {};
    const items = ps.subselection?.subselection_item ?? [];

    for (const [itemId, quantity] of Object.entries(groupSelections)) {
      if (quantity <= 0) continue;
      const item = items.find(i => i.id === itemId);
      if (!item) continue;
      result.push({
        subselectionItemId: item.id,
        name: item.name,
        price: item.price,
        quantity,
      });
    }
  }

  return result;
}

function isSubselectionValid(
  product: MenuSnapshotProduct,
  selections: Record<string, Record<string, number>>
): boolean {
  for (const ps of product.product_subselection ?? []) {
    const sub = ps.subselection;
    if (!sub) continue;

    const groupSelections = selections[ps.subselection_id] ?? {};
    const total = Object.values(groupSelections).reduce((sum, qty) => sum + qty, 0);

    if (total < sub.min_selection) return false;
    if (sub.max_selection > 0 && total > sub.max_selection) return false;
  }
  return true;
}

export function TotemProductSheet() {
  const { selectedProduct, cart, editingCartItemId } = useTotemCartState();
  const { setSelectedProduct, setEditingCartItemId } = useTotemActions();
  const { addProductToCart } = useProductInteraction();
  const [selections, setSelections] = useState<Record<string, Record<string, number>>>({});

  const product = selectedProduct;
  const open = Boolean(product);

  const priceFrom = useMemo(() => {
    if (!product || Number(product.price) > 0) return 0;
    let min = 0;
    for (const ps of product.product_subselection ?? []) {
      const sub = ps.subselection;
      if (!sub) continue;
      for (const item of sub.subselection_item ?? []) {
        const isOutOfStock = isProductOutOfStock(item.product_stock);
        if (!isOutOfStock && item.price > 0 && (min === 0 || item.price < min)) {
          min = item.price;
        }
      }
    }
    return min;
  }, [product]);

  const resetState = useCallback(() => {
    setSelections({});
    setEditingCartItemId(null);
  }, [setEditingCartItemId]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedProduct(null);
      resetState();
    }
  };

  const handleSubselectionChange = (subselectionId: string, itemId: string, quantity: number) => {
    setSelections(prev => ({
      ...prev,
      [subselectionId]: {
        ...(prev[subselectionId] ?? {}),
        [itemId]: quantity,
      },
    }));
  };

  // Pre-fill state when starting to edit an item. cart.items is read through a ref so that
  // later cart changes do not overwrite what the user is picking.
  const cartItemsRef = useRef(cart.items);
  useEffect(() => {
    cartItemsRef.current = cart.items;
  }, [cart.items]);

  useEffect(() => {
    if (!product || !editingCartItemId) return;
    const editingItem = cartItemsRef.current.find(i => i.id === editingCartItemId);
    if (!editingItem) return;
    const newSelections: Record<string, Record<string, number>> = {};
    for (const ps of product.product_subselection ?? []) {
      const sub = ps.subselection;
      if (!sub) continue;
      for (const item of sub.subselection_item ?? []) {
        const cartSub = editingItem.subselections.find(s => s.subselectionItemId === item.id);
        if (cartSub) {
          if (!newSelections[ps.subselection_id]) newSelections[ps.subselection_id] = {};
          newSelections[ps.subselection_id][item.id] = cartSub.quantity;
        }
      }
    }
    setSelections(newSelections);
  }, [product, editingCartItemId]);

  const subselections = useMemo(
    () => (product ? buildSubselectionPayload(product, selections) : []),
    [product, selections]
  );

  const isValid = product ? isSubselectionValid(product, selections) : false;

  const stockLimit = useMemo(() => {
    if (!product) return Infinity;
    return getProductStockLimit(product.product_stock);
  }, [product]);

  const existingInCart = useMemo(() => {
    if (!product) return 0;
    return cart.items
      .filter(i => i.productId === product.id && i.id !== editingCartItemId)
      .reduce((sum, i) => sum + i.quantity, 0);
  }, [product, cart.items, editingCartItemId]);

  const isStockAvailable = existingInCart < stockLimit;

  const handleAdd = () => {
    if (!product || !isValid || !isStockAvailable) return;

    if (editingCartItemId) {
      const editingItem = cart.items.find(i => i.id === editingCartItemId);
      const quantity = editingItem?.quantity ?? 1;

      cart.updateItem(editingCartItemId, {
        productId: product.id,
        name: getProductDisplayName(product),
        imageUrl: product.imageurl,
        unitPrice: Number(product.price),
        quantity,
        subselections,
      });
    } else {
      addProductToCart(product, 1, subselections);
    }

    setSelectedProduct(null);
    resetState();
  };

  if (!product) return null;

  const name = getProductDisplayName(product);
  const description = getProductDescription(product).trim();

  let groupCounter = 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[95vh] w-[95vw] flex-col gap-0 overflow-hidden rounded-2xl border-0 bg-background/95 p-0 backdrop-blur-md sm:max-w-xl md:max-w-2xl"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{name}</DialogTitle>
        <DialogDescription className="sr-only">Personaliza tu {name}</DialogDescription>

        <div className="flex h-full max-h-[90vh] min-h-[50vh] flex-col">
          {/* Custom Header */}
          <div className="z-10 flex shrink-0 flex-col items-start gap-3 border-b bg-background px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:gap-6 sm:px-6">
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              className="flex shrink-0 items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-slate-700 transition-colors hover:bg-slate-200 sm:px-4 sm:py-2"
            >
              <ArrowLeft className="size-4 sm:size-5" />
              <span className="text-sm font-semibold sm:text-base">Volver</span>
            </button>

            <div className="flex w-full min-w-0 flex-1 items-center gap-3 sm:gap-4">
              {product.imageurl ? (
                <div className="relative size-14 shrink-0 overflow-hidden rounded-xl shadow-sm sm:size-16">
                  <Image alt={name} className="object-cover" fill sizes="100px" src={product.imageurl} />
                </div>
              ) : (
                <div className="size-14 shrink-0 rounded-xl bg-muted sm:size-16" />
              )}

              <div className="flex min-w-0 flex-1 flex-col">
                <h2 className="truncate text-lg leading-tight font-bold sm:text-xl sm:text-balance sm:whitespace-normal">
                  {name}
                </h2>
                {description && (
                  <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
                )}
                <p className="mt-1 text-xl font-black text-amber-500 sm:text-2xl">
                  {Number(product.price) === 0 && priceFrom > 0
                    ? `Desde ${formatTotemPrice(priceFrom)}`
                    : formatTotemPrice(Number(product.price))}
                </p>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-muted/10 p-6 md:p-8">
            <div className="flex flex-col gap-10">
              {(product.product_subselection ?? []).map(ps => {
                const sub = ps.subselection;
                if (!sub) return null;
                const items = (sub.subselection_item ?? []).filter(i => shouldShowProduct(i));

                groupCounter++;

                return (
                  <SubselectionGroup
                    key={ps.subselection_id}
                    groupIndex={groupCounter}
                    items={items}
                    maxSelection={sub.max_selection}
                    minSelection={sub.min_selection}
                    name={sub.name}
                    selected={selections[ps.subselection_id] ?? {}}
                    single={sub.single}
                    onChange={(itemId, qty) => handleSubselectionChange(ps.subselection_id, itemId, qty)}
                  />
                );
              })}
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-end border-t bg-background p-4 sm:p-6 md:p-8">
            <Button
              className="flex h-12 items-center gap-2 rounded-full border-2 border-emerald-500/20 bg-white px-5 text-base font-bold text-emerald-600 shadow-md transition-all hover:bg-emerald-50 active:scale-[0.98] sm:h-16 sm:gap-3 sm:px-8 sm:text-lg"
              disabled={!isValid || !isStockAvailable}
              type="button"
              onClick={handleAdd}
            >
              {editingCartItemId ? 'Guardar cambios' : isStockAvailable ? 'Continuar' : 'Sin stock disponible'}
              <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white sm:size-8">
                <ArrowRight className="size-4 sm:size-5" />
              </div>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
